// =============================================================================
// Shared-calendar settings sheet (the gear icon): my label in this calendar,
// public holidays, reminders + daily digest, the member list, the invite
// link (owner) and leave / delete. Calendar.js loads the member list and
// invite URL into `sharedSettings` before opening it.
// =============================================================================

import React from 'react';
import { X, Trash2, Copy, LogOut, Crown } from 'lucide-react';
import { useToast } from '../../lib/Toast';
import { useTranslation } from '../../lib/i18n';
import { useConfirm } from '../../lib/ConfirmDialog';
import { useAuth } from '../../lib/AuthContext';
import { deleteSharedCalendar, leaveSharedCalendar, setMyCalendarCategory } from '../../lib/sharedCalendars';
import { REMINDER_OPTIONS, reminderLabel, BIRTHDAY_AHEAD_OPTIONS, birthdayAheadLabel } from '../../lib/notificationPrefs';
import { AVATAR_COLORS } from '../../lib/profile';

const SharedCalendarSheet = ({
  sharedSettings,
  setSharedSettings,
  currentSharedCalendar,
  sharedCalendarId,
  selectableCategories,
  categories,
  membersById,
  setMembersById,
  holidaySettings,
  updateHolidaySettings,
  birthdaySettings,
  saveSharedNotifications,
  reload
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { user } = useAuth();
  const toast = useToast();
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('sharedCalendar.settings')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {currentSharedCalendar.name}
            </p>
          </div>
          <button
            onClick={() => setSharedSettings(null)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* My label in this calendar — defaults the colour for any
            event you create here (Quick-Add and the modal). */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('sharedCalendar.myLabel')}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
            {selectableCategories.map((c) => {
              const myCat =
                (user && membersById[user.id]?.categoryId) || null;
              const active = myCat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={async () => {
                    try {
                      await setMyCalendarCategory(
                        sharedCalendarId,
                        active ? null : c.id
                      );
                      // Optimistic local update so the dot reflects
                      // immediately; the next fetch reconciles.
                      setMembersById((prev) => {
                        if (!user) return prev;
                        const me = prev[user.id] || { userId: user.id };
                        return {
                          ...prev,
                          [user.id]: {
                            ...me,
                            categoryId: active ? null : c.id
                          }
                        };
                      });
                    } catch (err) {
                      toast.error(err.message || t('common.error'));
                    }
                  }}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    active
                      ? 'border-transparent text-white'
                      : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  style={
                    active ? { backgroundColor: c.color } : undefined
                  }
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  {c.name}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('sharedCalendar.myLabelHint')}
          </p>
        </div>

        {/* Austrian public holidays in this calendar, with the label
            colour reserved for them (the same setting as in the label
            manager). */}
        <div className="mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t('calendar.holidays', 'Österreichische Feiertage')}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('calendar.holidaysHint')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!holidaySettings.enabled}
              onClick={() => updateHolidaySettings({ enabled: !holidaySettings.enabled })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                holidaySettings.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  holidaySettings.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {holidaySettings.enabled && (
            <div className="mt-2 flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
              {categories
                .filter((c) => !(birthdaySettings.linkedToCalendar && c.id === birthdaySettings.categoryId))
                .map((c) => {
                  const active = holidaySettings.categoryId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => updateHolidaySettings({ categoryId: c.id })}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                        active
                          ? 'border-transparent text-white'
                          : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      style={active ? { backgroundColor: c.color } : undefined}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                      {active ? t('calendar.holidaysLabel', 'Feiertage') : c.name}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {/* Reminders + daily digest — every member gets them; each can
            mute them for themselves under Settings → Notifications. */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('notify.title')}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                  {t('notify.digest')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('notify.digestHint')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="time"
                  className="input !w-auto"
                  value={currentSharedCalendar.digestTime || ''}
                  onChange={(e) => saveSharedNotifications({ digestTime: e.target.value || null })}
                  aria-label={t('notify.digest')}
                />
                {currentSharedCalendar.digestTime && (
                  <button
                    type="button"
                    onClick={() => saveSharedNotifications({ digestTime: null })}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600"
                  >
                    {t('notify.off')}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {t('notify.reminder')}
              </p>
              <select
                className="input !w-auto flex-shrink-0"
                value={currentSharedCalendar.reminderMinutes || 0}
                onChange={(e) => saveSharedNotifications({ reminderMinutes: Number(e.target.value) || null })}
                aria-label={t('notify.reminder')}
              >
                {REMINDER_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {reminderLabel(m, t)}
                  </option>
                ))}
              </select>
            </div>
            {currentSharedCalendar.hasBirthdays && (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {t('notify.birthdayAhead')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('notify.birthdayAheadHint')}
                  </p>
                </div>
                <select
                  className="input !w-auto flex-shrink-0"
                  value={currentSharedCalendar.birthdayReminderDays || 0}
                  onChange={(e) => saveSharedNotifications({ birthdayReminderDays: Number(e.target.value) || null })}
                  aria-label={t('notify.birthdayAhead')}
                >
                  {BIRTHDAY_AHEAD_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {birthdayAheadLabel(d, t)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('notify.sharedHint')}
          </p>
        </div>

        {/* Members */}
        <div>
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('sharedCalendar.membersTitle')}
          </div>
          {sharedSettings.loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
              {t('sharedCalendar.loadingMembers')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
              {sharedSettings.members.map((m) => {
                const isMe = user && m.userId === user.id;
                const color =
                  AVATAR_COLORS.find((c) => c.id === m.avatarColor) ||
                  AVATAR_COLORS[0];
                const initial = (m.displayName || m.email || '?')
                  .trim()
                  .charAt(0)
                  .toUpperCase();
                return (
                  <li
                    key={m.userId}
                    className="flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-gray-900"
                  >
                    {m.avatarKind === 'photo' && m.avatarUrl ? (
                      <img
                        src={m.avatarUrl}
                        alt=""
                        className="flex-shrink-0 w-9 h-9 rounded-full object-cover shadow-sm"
                      />
                    ) : (
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm"
                        style={{ backgroundColor: color.swatch }}
                      >
                        {m.avatarEmoji ? (
                          <span className="text-lg leading-none">{m.avatarEmoji}</span>
                        ) : (
                          initial
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate flex items-center gap-1.5">
                        {m.categoryId &&
                          (() => {
                            const memberCat = selectableCategories.find(
                              (c) => c.id === m.categoryId
                            );
                            if (!memberCat) return null;
                            return (
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: memberCat.color }}
                                title={memberCat.name}
                              />
                            );
                          })()}
                        <span className="truncate">
                          {m.displayName || m.userId.slice(0, 8)}
                        </span>
                        {isMe && (
                          <span className="ml-1 text-xs text-gray-400 flex-shrink-0">
                            ({t('sharedCalendar.you')})
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        m.isOwner
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {m.isOwner && <Crown size={12} />}
                      {m.isOwner ? t('sharedCalendar.owner') : t('sharedCalendar.member')}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Invite link (owner only) */}
        {currentSharedCalendar.isOwner && sharedSettings.inviteUrl && (
          <div className="mt-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t('sharedCalendar.invite')}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('sharedCalendar.shareHint')}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                className="input flex-1 font-mono text-xs"
                value={sharedSettings.inviteUrl}
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(sharedSettings.inviteUrl);
                    setSharedSettings((s) => (s ? { ...s, copied: true } : s));
                    setTimeout(
                      () => setSharedSettings((s) => (s ? { ...s, copied: false } : s)),
                      1800
                    );
                  } catch (_) {}
                }}
                className="btn-secondary flex items-center gap-2"
              >
                <Copy size={16} />
                {sharedSettings.copied ? t('sharedCalendar.copied') : t('sharedCalendar.copyLink')}
              </button>
            </div>
          </div>
        )}

        {/* Danger zone */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-2 justify-end">
          {!currentSharedCalendar.isOwner && (
            <button
              onClick={async () => {
                if (!(await confirm({ message: t('sharedCalendar.leaveConfirm'), danger: true }))) return;
                try {
                  await leaveSharedCalendar(sharedCalendarId);
                  setSharedSettings(null);
                  if (reload) await reload();
                } catch (err) {
                  window.alert(err.message || t('sharedCalendar.leaveFailed'));
                }
              }}
              className="btn-secondary flex items-center gap-2 text-red-600"
            >
              <LogOut size={16} />
              {t('sharedCalendar.leave')}
            </button>
          )}
          {currentSharedCalendar.isOwner && (
            <button
              onClick={async () => {
                if (!(await confirm({ message: t('sharedCalendar.deleteConfirm'), danger: true }))) return;
                try {
                  await deleteSharedCalendar(sharedCalendarId);
                  setSharedSettings(null);
                  if (reload) await reload();
                } catch (err) {
                  window.alert(err.message || t('sharedCalendar.deleteFailed'));
                }
              }}
              className="btn-secondary flex items-center gap-2 text-red-600"
            >
              <Trash2 size={16} />
              {t('sharedCalendar.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedCalendarSheet;
